import type { DateRange } from "react-day-picker";

/**
 * Format a currency value with compact notation.
 */
export function formatCompactCurrency(value: number, currencyCode: string): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currencyCode,
    notation: "compact",
    maximumFractionDigits: 2,
  }).format(value);
}

/**
 * Format a currency value with full precision.
 */
export function formatCurrency(value: number, currencyCode: string): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currencyCode,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

/**
 * Format a decimal value as a percentage string.
 */
export function formatPercent(value: number, fractionDigits = 2): string {
  return `${(value * 100).toFixed(fractionDigits)}%`;
}

/**
 * Format X-axis timestamp based on the period.
 */
export function formatXAxis(timestamp: string, period: string): string {
  const date = new Date(timestamp);
  if (period === "1d") {
    return date.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
  }
  if (period === "1w" || period === "1m") {
    return date.toLocaleDateString(undefined, { month: "short", day: "numeric" });
  }
  return date.toLocaleDateString(undefined, { month: "short", year: "2-digit" });
}

/**
 * Format chart tooltip timestamp based on the selected period.
 */
export function formatChartTooltipLabel(timestamp: string, period: string): string {
  const date = new Date(timestamp);

  if (period === "1d") {
    return date.toLocaleTimeString(undefined, {
      hour: "2-digit",
      minute: "2-digit",
    });
  }

  if (period === "1w" || period === "1m") {
    return date.toLocaleString(undefined, {
      weekday: "short",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  }

  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

/**
 * Format Y-axis currency value with compact notation.
 */
export function formatYAxis(value: number, currencyCode: string): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currencyCode,
    notation: "compact",
    maximumFractionDigits: 1,
  }).format(value);
}

/**
 * Format a date range for display.
 */
export function formatDateRange(range: DateRange | undefined): string {
  if (!range?.from) return "Select range";
  
  const formatDate = (date: Date) =>
    date.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
  
  if (!range.to) {
    return `${formatDate(range.from)} – Now`;
  }
  
  return `${formatDate(range.from)} – ${formatDate(range.to)}`;
}
